import {BlurEvent, ChangeEvent, DropdownItem, Snackbar, Textbox, Switch} from "@mcleod/components";
import { AutogenLayoutDistanceOptions } from "./autogen/AutogenLayoutDistanceOptions";
import {Api, StringUtil} from "@mcleod/core";
import {CommonDialogs} from "@mcleod/common";

export const MILEMAKER = "E";
export const PCMILER_WEB = "T";
export const PCMILER_ON_PREM = "A";
export const INTELLIROUTE = "R";

export const MILEMAKER_LOOKUP_TYPES = [new DropdownItem("P", 'Practical'),
    new DropdownItem("G", 'HHG'),
    new DropdownItem("T", 'Quickest')];

export const MILEMAKER_GUIDE19_METHODS = [new DropdownItem("C", "City/State"),
    new DropdownItem("Z", "Zip Code"),
    new DropdownItem("L", "Lat/long")];

export const MILEMAKER_GUIDE20_METHODS = [new DropdownItem("C", "City/State"),
    new DropdownItem("Z", "Zip Code"),
    new DropdownItem("L", "Lat/long"),
    new DropdownItem("A", "Address")];

export const PCMILER_LOOKUP_TYPES = [new DropdownItem("L", "Lat/Long"),
    new DropdownItem("P", "Practical"),
    new DropdownItem("S", "Shortest")];

export const PCMILER_WEB_LOOKUP_METHODS = [new DropdownItem("A", "Address"),
    new DropdownItem("C", "City/State"),
    new DropdownItem("Z", "Zip Code")];

export const PCMILER_ON_PREM_LOOKUP_METHODS = [new DropdownItem("C", "City/State"),
    new DropdownItem("Z", "Zip Code")];

export const INTELLIROUTE_LOOKUP_TYPES = [new DropdownItem("L", "Lat/Long"),
    new DropdownItem("P", "Practical"),
    new DropdownItem("S", "Shortest"),
    new DropdownItem("Q", "Quick"),
    new DropdownItem("C", "Low Cost")];

export const INTELLIROUTE_LOOKUP_METHODS = [new DropdownItem("C", "City/State"),
    new DropdownItem("Z", "Zip Code")];

export class DistanceOptions extends AutogenLayoutDistanceOptions {

    async postDataSources(): Promise<boolean> {
        try {
            if (this.mainDataSource?.hasChanged()) {
                await this.mainDataSource.post();
            }
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    textboxVendorOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const vendor = textbox.selectedItem?.value;
        this.setVendorSpecificOptions(vendor);
        const vendorHasChanged: boolean =
            !StringUtil.isEmptyString(event.oldValue) && event.oldValue !== textbox.selectedItem.displayValue;
        if (vendorHasChanged) {
            (this.findComponentById("switchFiftyThreeTrailer") as Switch).checked = true;
            Snackbar.showWarningSnackbar("Please ensure the distance options are correct when changing vendors.", {
                targetPanel: this
            });
        }
    }

    setVendorSpecificOptions(vendor: string) {
        if (vendor === PCMILER_WEB || vendor === PCMILER_ON_PREM) {
            const isWeb = vendor === PCMILER_WEB;
            this.panelIntellirouteDistanceOptions.visible = false;
            this.panelPCMilerDistanceOptions.visible = true;
            this.panelMMDistanceOptions.visible = false;
            (this.findComponentById("textboxMMVersion") as Textbox).required = false;
            (this.findComponentById("textboxPCMilerVersion") as Textbox)?.setProps({ required: isWeb, visible: isWeb });

            // Set lookup types/methods
            (this.findComponentById("textboxLookupType") as Textbox).items = PCMILER_LOOKUP_TYPES;
            (this.findComponentById("textboxMethod") as Textbox).items = isWeb ? PCMILER_WEB_LOOKUP_METHODS : PCMILER_ON_PREM_LOOKUP_METHODS;
        } else if (vendor === INTELLIROUTE) {
            this.panelIntellirouteDistanceOptions.visible = true;
            this.panelPCMilerDistanceOptions.visible = false;
            this.panelMMDistanceOptions.visible = false;
            (this.findComponentById("textboxMMVersion") as Textbox).required = false;
            (this.findComponentById("textboxPCMilerVersion") as Textbox).required = false;

            // Set lookup types/methods
            (this.findComponentById("textboxLookupType") as Textbox).items = INTELLIROUTE_LOOKUP_TYPES;
            (this.findComponentById("textboxMethod") as Textbox).items = INTELLIROUTE_LOOKUP_METHODS;
        } else if (vendor === MILEMAKER) {
            this.panelIntellirouteDistanceOptions.visible = false;
            this.panelPCMilerDistanceOptions.visible = false;
            this.panelMMDistanceOptions.visible = true;
            (this.findComponentById("textboxMMVersion") as Textbox).required = true;
            (this.findComponentById("textboxPCMilerVersion") as Textbox).required = false;

            const mm_version = (this.findComponentById("textboxMMVersion") as Textbox).valueAsString;
            if (mm_version === "guide20") {
                this.panelMMGuide19Options.visible = false;
                this.panelMMGuide20Options.visible = true;

                // Set lookup types/methods
                (this.findComponentById("textboxLookupType") as Textbox).items = MILEMAKER_LOOKUP_TYPES;
                (this.findComponentById("textboxMethod") as Textbox).items = MILEMAKER_GUIDE20_METHODS;
            } else { // guide19 or undefined
                // Default to guide19 options
                this.panelMMGuide19Options.visible = true;
                this.panelMMGuide20Options.visible = false;

                // Set lookup types/methods
                (this.findComponentById("textboxLookupType") as Textbox).items = MILEMAKER_LOOKUP_TYPES;
                (this.findComponentById("textboxMethod") as Textbox).items = MILEMAKER_GUIDE19_METHODS;
            }
        }
    }

    textboxMmG20VehicleTypeOnChange(event: ChangeEvent) {
        const isTruck = event.newValue === "Truck";

        this.textboxMmVehicleWeight.visible = isTruck;
        this.textboxMmG20TrailerOption.visible = isTruck;
        this.textboxMmVehicleAxleCount.visible = isTruck;
        this.textboxMmHeightFt.visible = isTruck;
        this.textboxMmHeightIn.visible = isTruck;
        this.textboxMmWidthFt.visible = isTruck;
        this.textboxMmWidthIn.visible = isTruck;
    }

    textboxLookupTypeOnChange(event: ChangeEvent) {
        const newValue = event.newValue;

        const isEnabled = newValue !== "Lat/Long";
        this.setMethodVisibilityAndRequirement(this.textboxMethod, isEnabled);

    }

    setMethodVisibilityAndRequirement(textboxMethod: Textbox, isEnabled: boolean) {
        if(!isEnabled) {
            textboxMethod.setSelectedItemFromDisplayValue(null);
        }
        textboxMethod.enabled = isEnabled;
        textboxMethod.visible = isEnabled;
        textboxMethod.required = isEnabled;
        textboxMethod.requiredDuringAdd = isEnabled;
        textboxMethod.requiredDuringUpdate = isEnabled;
    }

    // Listener to display the proper fields when the value is changed
    textboxMMVersionOnChange(event: ChangeEvent) {
        const isGuide19 = event.newValue === 'guide19';

        this.panelMMGuide19Options.visible = isGuide19;
        this.panelMMGuide20Options.visible = !isGuide19;

        // Set lookup types/methods
        (this.findComponentById("textboxLookupType") as Textbox).items = MILEMAKER_LOOKUP_TYPES;
        (this.findComponentById("textboxMethod") as Textbox).items = isGuide19 ? MILEMAKER_GUIDE19_METHODS : MILEMAKER_GUIDE20_METHODS;

        this.textboxMMVersion.setSelectedItemFromValue(isGuide19 ? 'guide19' : 'guide20');
        if (!this.textboxLookupType.selectedItem) {
            this.textboxLookupType.setSelectedItemFromDisplayValue('Practical');
        }
    }

    private async textboxProfileIdOnBlur(event: BlurEvent) {
        const title = (event.target as Textbox).text;
        if (title.length === 0) {
            return;
        }

        const response = await Api.post("lme/dispatch/check-duplicate-code", {id: title, table: "distance_profile"});

        if(response?.data[0].is_duplicate) {
            Snackbar.showWarningSnackbar("The value " + title + " already exists in the table");
            this.textboxProfileId.text = null;
            this.textboxProfileId.focus();
        }
    }
}
